import React, { useCallback } from 'react';
import { default as Form } from '../../components/connectedForm';
import Label from '../../components/connectedForm/Label';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { DateField } from '../../components/connectedForm/inputComponents';
import getMakeableQuantity from '../salesLedger/tradeCounter/util/getMakeableQuantity';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import HintIcon from '../../components/connectedForm/HintIcon';
import { IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky';
import { getTheme } from '@fluentui/react';
import { makeStyles } from '@fluentui/react-components';
import { AssemblySubformContextType, useAssemblyTreeContext } from './AssemblyTreeContext';

interface TooltipLabelProps {
  labelText?: string;
  hint: string;
  children?: any;
}

const TooltipLabel = (props: TooltipLabelProps) => {
  const { hint } = props;
  const labelText = props.labelText ?? props.children;
  return (
    <TooltipHost content={hint}>
      <Label>
        <span>
          {labelText}
          <HintIcon hint={hint} />
        </span>
      </Label>
    </TooltipHost>
  );
};

interface IScrollablePaneExampleItem {
  title: string;
  content: React.ReactNode;
  index: number;
}

const theme = getTheme();

const useStyles = makeStyles({
  wrapper: {
    height: '40vh',
    position: 'relative',
    maxHeight: 'inherit',
  },
  pane: {
    maxWidth: '100%',
    border: '1px solid ' + theme.palette.neutralLight,
  },
  sticky: {
    padding: '5px 20px 5px 10px',
    fontSize: '13px',
    borderTop: '1px solid ' + theme.palette.black,
    borderBottom: '1px solid ' + theme.palette.black,
  },
  textContent: {
    padding: '15px 10px',
    backgroundColor: 'white',
  },
});

const AssemblySubForm = () => {
  const { reservationAssembly: assembly, assemblyComponentInformation } =
    useAssemblyTreeContext() as AssemblySubformContextType;

  const assemblyInfoPane = useCallback(() => {
    return (
      <Form.FormContainer>
        <Form.Fieldset cols={1}></Form.Fieldset>
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Label hint={assembly?.product?.description}>Order Number</Label>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2}>
            <BlurTextField
              data-testid="assembly-number"
              value={assembly?.orderNumber}
              spellCheck="false"
              readOnly={true}
              includeLabel={false}
            />
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <TooltipLabel hint={assembly?.product?.description ?? ''}>Description</TooltipLabel>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2}>
            <BlurTextField
              data-testid="assembly-warehouse"
              value={assembly?.product?.description}
              spellCheck="false"
              readOnly={true}
              includeLabel={false}
            />
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Label>Qty</Label>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2}>
            <BlurTextField
              data-testid="assembly-qty"
              value={assembly?.quantity}
              decimals={assembly?.product?.precision ?? 0}
              spellCheck="false"
              readOnly={true}
              includeLabel={false}
            />
          </Form.Fieldset>
        </Form.Row>
        {assembly?.units && (
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <Label>Units</Label>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="assembly-units"
                value={assembly?.units?.description ?? ''}
                decimals={assembly?.units?.precision ?? 0}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
        )}
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Label>Order Date</Label>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2}>
            <DateField
              data-testid="assembly-orderdate"
              value={assembly?.orderDate}
              inputCols={1}
              readOnly={true}
              includeLabel={false}
            />
          </Form.Fieldset>
        </Form.Row>
        {assembly?.nextProductionDate && (
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <Label>Production Date</Label>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <DateField
                data-testid="assembly-nextproductiondate"
                inputCols={1}
                value={assembly.nextProductionDate}
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
        )}
        {assembly?.expectedCompletionDate && (
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <Label>Completion Date</Label>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <DateField
                data-testid="assembly-expectedcompletiondate"
                inputCols={1}
                value={assembly.expectedCompletionDate}
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
        )}
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Label>Makeable</Label>
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2}>
            <BlurTextField
              data-testid="assembly-makeable-qty"
              value={assembly?.product && getMakeableQuantity(assembly?.product, assembly?.warehouse?.id)}
              decimals={assembly?.product?.precision ?? 0}
              spellCheck="false"
              readOnly={true}
              includeLabel={false}
            />
          </Form.Fieldset>
        </Form.Row>
      </Form.FormContainer>
    );
  }, [assembly]);

  const stockInfoPane = () => {
    return (
      <Form.FormContainer>
        <Form.Fieldset cols={1}>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'Stock that is not reserved to anything and is available for use'}>
                Free Stock
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-free-stock"
                value={assemblyComponentInformation?.stockTotal?.freeStock}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'Current physical level of stock including allocations, reservations, etc.'}>
                Level
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-level"
                value={assemblyComponentInformation?.stockTotal?.level}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'Stock that is currently on order'}>On Order</TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-onorder"
                value={assemblyComponentInformation?.stockTotal?.onOrder}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel
                hint={'The amount of stock on order that has been reserved to something, i.e. a sales order'}
              >
                Assigned
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-assigned"
                value={assemblyComponentInformation?.stockTotal?.assigned}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'Amount of kit or assembly currently in production'}>In Production</TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-in-production"
                value={assemblyComponentInformation?.stockTotal?.inProduction}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel
                hint={
                  'The current level of stock, plus everything on order minus stock required to satisfy outstanding orders'
                }
              >
                Forecasted
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-forecasted"
                value={assemblyComponentInformation?.stockTotal?.forecasted}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'Amount of stock makeable from current available stock'}>Makeable</TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-makeable"
                value={assemblyComponentInformation?.stockTotal?.makeable}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel
                hint={
                  'The amount of stock required to fulfil all sales orders including anything that is allocated or assigned'
                }
              >
                Outstanding
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-outstanding"
                value={assemblyComponentInformation?.stockTotal?.outstanding}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'Amount of stock allocated to sales order'}>Allocated</TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-allocated"
                value={assemblyComponentInformation?.stockTotal?.allocated}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'The amount of stock that has been reserved for assemblies or anything else'}>
                Reserved
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-reserved"
                value={assemblyComponentInformation?.stockTotal?.reserved}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel
                hint={
                  'Components reserved for kits on sales orders, components reserved for assemblies, and products on stock transfer lines that are not marked as complete'
                }
              >
                Issued
              </TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-issued"
                value={assemblyComponentInformation?.stockTotal?.issued}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <TooltipLabel hint={'The amount of stock that has been put onto a picking list'}>Picked</TooltipLabel>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <BlurTextField
                data-testid="stock-picked"
                value={assemblyComponentInformation?.stockTotal?.picked}
                decimals={assemblyComponentInformation?.component?.precision}
                spellCheck="false"
                readOnly={true}
                includeLabel={false}
              />
            </Form.Fieldset>
          </Form.Row>
        </Form.Fieldset>
      </Form.FormContainer>
    );
  };

  const styles = useStyles();
  const scrollablePaneStyles: Partial<IScrollablePaneStyles> = { root: styles.pane };
  const items = [
    {
      title: 'Stock Information',
      content: stockInfoPane(),
      index: 1,
    },
    {
      title: 'Assembly Information',
      content: assemblyInfoPane(),
      index: 2,
    },
  ];

  const createContentArea = (item: IScrollablePaneExampleItem) => (
    <div
      key={item.index}
      style={{
        backgroundColor: 'lightgrey',
      }}
    >
      <Sticky stickyPosition={StickyPositionType.Both}>
        <div role="heading" aria-level={1} className={styles.sticky}>
          {item.title}
        </div>
      </Sticky>
      <div className={styles.textContent}>{item.content}</div>
    </div>
  );

  const contentAreas = items.map(createContentArea);
  return (
    <div
      style={{
        height: '40%',
        position: 'relative',
        maxHeight: 'inherit',
      }}
    >
      <ScrollablePane
        scrollContainerFocus={true}
        scrollContainerAriaLabel={'Stock Component Example'}
        styles={scrollablePaneStyles}
      >
        {contentAreas}
      </ScrollablePane>
    </div>
  );
};

export default AssemblySubForm;
